import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container,
  Avatar,
  Stack,
  Fade,
  Zoom,
  alpha,
  useTheme
  // useMediaQuery,
} from '@mui/material';
import {
  PlayArrow,
  // Stop,
  Download,
  Refresh,
  Security,
  Warning,
  CheckCircle,
  // Error,
  ExpandMore,
  Visibility,
  // Settings,
  Speed,
  Shield,
  BugReport,
  Psychology,
  PrivacyTip,
  ContentCopy,
  Assessment
  // Code,
  // Info,
  // Close,
  // Add,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';

interface ScanConfig {
  endpoint: string;
  modelType: 'openai' | 'anthropic' | 'huggingface' | 'custom';
  modelName?: string;
  scanTypes: string[];
  headers: { [key: string]: string };
  timeout: number;
  requestDelay: number;
  maxTokens: number;
  temperature: number;
  customParameters?: { [key: string]: any };
}

interface ScanResult {
  id: string;
  status: 'running' | 'completed' | 'failed';
  progress: number;
  startTime: number;
  endTime?: number;
  duration?: number;
  results: {
    totalTests: number;
    passedTests: number;
    failedTests: number;
    vulnerabilities: Array<{
      category: string;
      test: string;
      severity: 'low' | 'medium' | 'high' | 'critical';
      description: string;
      evidence: string;
      recommendation: string;
    }>;
    recommendations: string[];
    categories: { [key: string]: any };
  };
  error?: string;
}

interface ScanTemplate {
  name: string;
  modelType: string;
  headers: { [key: string]: string };
  endpoint: string;
  modelName?: string;
  maxTokens: number;
  temperature?: number;
  scanTypes: string[];
}

const ModelScanner: React.FC = () => {
  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeScans, setActiveScans] = useState<ScanResult[]>([]);
  const [completedScans, setCompletedScans] = useState<ScanResult[]>([]);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedScan, setSelectedScan] = useState<ScanResult | null>(null);
  const [templates, setTemplates] = useState<{ [key: string]: ScanTemplate }>({});
  const [scanTypes, setScanTypes] = useState<{ [key: string]: any }>({});
  const [loading, setLoading] = useState(false);

  // Injection database state
  const [databaseStats, setDatabaseStats] = useState<any>(null);
  const [databaseDialogOpen, setDatabaseDialogOpen] = useState(false);
  const [testPrompt, setTestPrompt] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [testLoading, setTestLoading] = useState(false);

  const [config, setConfig] = useState<ScanConfig>({
    endpoint: '',
    modelType: 'custom',
    modelName: '',
    scanTypes: ['prompt_injection', 'jailbreak', 'content_moderation'],
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000,
    requestDelay: 1000,
    maxTokens: 1000,
    temperature: 0.7,
    customParameters: {}
  });

  const [headerInput, setHeaderInput] = useState('');
  const [paramInput, setParamInput] = useState('');

  useEffect(() => {
    fetchTemplates();
    fetchUserScans();
    fetchDatabaseStats();

    // Set up polling for active scans
    const interval = setInterval(() => {
      if (activeScans.length > 0) {
        updateActiveScanStatuses();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [activeScans.length]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/model-scan/templates');
      setTemplates(response.data.data.templates);
      setScanTypes(response.data.data.scanTypes);
    } catch (error: any) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load scan templates');
    }
  };

  const fetchUserScans = async () => {
    try {
      const response = await axios.get('/api/model-scan/scans');
      const scans = response.data.data;

      setActiveScans(scans.filter((scan: any) => scan.status === 'running'));
      setCompletedScans(scans.filter((scan: any) => scan.status !== 'running'));
    } catch (error: any) {
      console.error('Failed to fetch scans:', error);
    }
  };

  const fetchDatabaseStats = async () => {
    try {
      const response = await axios.get('/api/injection-database/stats');
      setDatabaseStats(response.data.data);
    } catch (error: any) {
      console.error('Failed to fetch database stats:', error);
    }
  };

  const testPromptInjection = async () => {
    if (!testPrompt.trim()) {
      toast.error('Please enter a prompt to test');
      return;
    }

    setTestLoading(true);
    try {
      const response = await axios.post('/api/injection-database/test-prompt', {
        prompt: testPrompt,
        testMode: true
      });
      setTestResult(response.data.data);
      toast.success('Prompt analysis completed');
    } catch (error: any) {
      console.error('Failed to test prompt:', error);
      toast.error('Failed to analyze prompt');
    } finally {
      setTestLoading(false);
    }
  };

  const updateActiveScanStatuses = async () => {
    try {
      const updatedScans = await Promise.all(
        activeScans.map(async (scan) => {
          try {
            const response = await axios.get(`/api/model-scan/status/${scan.id}`);
            return response.data.data;
          } catch (error) {
            return scan; // Keep original if fetch fails
          }
        })
      );

      const stillActive = updatedScans.filter((scan) => scan.status === 'running');
      const nowCompleted = updatedScans.filter((scan) => scan.status !== 'running');

      setActiveScans(stillActive);

      if (nowCompleted.length > 0) {
        setCompletedScans((prev) => [...nowCompleted, ...prev]);
        nowCompleted.forEach((scan) => {
          if (scan.status === 'completed') {
            toast.success(
              `Scan completed: ${scan.results?.vulnerabilities?.length || 0} vulnerabilities found`
            );
          } else if (scan.status === 'failed') {
            toast.error(`Scan failed: ${scan.error}`);
          }
        });
      }
    } catch (error) {
      console.error('Failed to update scan statuses:', error);
    }
  };

  const startScan = async () => {
    try {
      setLoading(true);

      // Validation
      if (!config.endpoint || !config.endpoint.trim()) {
        toast.error('Please enter a valid endpoint URL');
        setLoading(false);
        return;
      }

      if (config.scanTypes.length === 0) {
        toast.error('Please select at least one scan type');
        setLoading(false);
        return;
      }

      console.log('Starting scan with config:', config);

      const response = await axios.post('/api/model-scan/start', config);

      console.log('Scan started response:', response.data);
      toast.success('Model scan started successfully!');
      setConfigDialogOpen(false);

      // Add to active scans
      const newScan: ScanResult = {
        id: response.data.scanId,
        status: 'running',
        progress: 0,
        startTime: Date.now(),
        results: {
          totalTests: 0,
          passedTests: 0,
          failedTests: 0,
          vulnerabilities: [],
          recommendations: [],
          categories: {}
        }
      };

      setActiveScans((prev) => [newScan, ...prev]);

      // Show helpful message
      toast.success('Scan is running. This may take 1-2 minutes...', { duration: 5000 });
    } catch (error: any) {
      console.error('Failed to start scan:', error);
      console.error('Error response:', error.response?.data);

      const errorMessage =
        error.response?.data?.message || error.response?.data?.error || 'Failed to start scan';

      toast.error(errorMessage);

      // Show validation errors if any
      if (error.response?.data?.details) {
        error.response.data.details.forEach((detail: any) => {
          toast.error(detail.msg || detail.message);
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = (templateKey: string) => {
    const template = templates[templateKey];
    if (template) {
      setConfig({
        ...config,
        endpoint: template.endpoint,
        modelType: template.modelType as any,
        modelName: template.modelName || '',
        headers: { ...template.headers },
        maxTokens: template.maxTokens,
        temperature: template.temperature || 0.7,
        scanTypes: [...template.scanTypes]
      });

      // Update header input display
      setHeaderInput(
        Object.entries(template.headers)
          .map(([key, value]) => `${key}: ${value}`)
          .join('\n')
      );
    }
  };

  const updateHeaders = () => {
    const headers: { [key: string]: string } = {};
    headerInput.split('\n').forEach((line) => {
      const [key, ...valueParts] = line.split(':');
      if (key && valueParts.length > 0) {
        headers[key.trim()] = valueParts.join(':').trim();
      }
    });
    setConfig({ ...config, headers });
  };

  const updateCustomParameters = () => {
    try {
      const params = paramInput ? JSON.parse(paramInput) : {};
      setConfig({ ...config, customParameters: params });
    } catch (error) {
      toast.error('Invalid JSON format for custom parameters');
    }
  };

  const exportResults = async (scanId: string, format: 'json' | 'csv' = 'json') => {
    try {
      const response = await axios.get(`/api/model-scan/export/${scanId}?format=${format}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `scan-${scanId}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Scan results exported as ${format.toUpperCase()}`);
    } catch (error: any) {
      console.error('Failed to export results:', error);
      toast.error('Failed to export scan results');
    }
  };

  const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' | 'success' => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'info';
    }
  };

  const getScanTypeIcon = (type: string) => {
    switch (type) {
      case 'prompt_injection':
        return <Security />;
      case 'jailbreak':
        return <Shield />;
      case 'content_moderation':
        return <Warning />;
      case 'pii_extraction':
        return <PrivacyTip />;
      case 'adversarial':
        return <BugReport />;
      case 'robustness':
        return <Speed />;
      default:
        return <Psychology />;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Enhanced Header Section */}
      <Box
        sx={{
          mb: 4,
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
          }
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: 'primary.main',
                border: `3px solid ${theme.palette.primary.main}`
              }}
            >
              <Security sx={{ fontSize: 36 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '1.75rem', sm: '2.5rem' },
                  mb: 1
                }}
              >
                Model Security Scanner
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
              >
                Comprehensive security testing for AI models against various attack vectors
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            onClick={() => setConfigDialogOpen(true)}
            disabled={activeScans.length >= 3}
            sx={{
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
              }
            }}
          >
            Start New Scan
          </Button>
        </Stack>
      </Box>

      {/* Quick Start Guide */}
      <Fade in timeout={500}>
        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Quick Start Guide:
          </Typography>
          <Typography variant="body2" component="div">
            1. Click "Start New Scan" and select a template (OpenAI, Anthropic, etc.)
            <br />
            2. Add your API key to the headers section
            <br />
            3. Select scan types and click "Start Scan"
            <br />
            4. Scans typically take 1-2 minutes to complete
            <br />
            <br />
            <strong>Note:</strong> You need a valid, responding API endpoint to scan. The scanner
            will test YOUR endpoint for security vulnerabilities.
          </Typography>
        </Alert>
      </Fade>

      {/* Active Scans */}
      {activeScans.length > 0 && (
        <Fade in timeout={600}>
          <Card
            elevation={2}
            sx={{
              mb: 3,
              borderRadius: 3,
              border: `2px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                p: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.15)} 100%)`,
                borderBottom: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: alpha(theme.palette.warning.main, 0.15),
                    color: 'warning.main',
                    border: `2px solid ${theme.palette.warning.main}`
                  }}
                >
                  <Speed sx={{ fontSize: 28 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Active Scans
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Scans currently in progress
                  </Typography>
                </Box>
                <Chip
                  label={`${activeScans.length} Running`}
                  color="warning"
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
            </Box>
            <CardContent sx={{ p: 3 }}>
              {activeScans.map((scan, index) => (
                <Zoom in timeout={300 + index * 100} key={scan.id}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 2,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      bgcolor: alpha(theme.palette.background.paper, 0.5),
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                    >
                      <Box flex={1}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, fontFamily: 'monospace' }}
                          >
                            Scan {scan.id.substring(0, 8)}...
                          </Typography>
                          <Chip
                            label="RUNNING"
                            size="small"
                            color="warning"
                            sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                          />
                        </Stack>
                        <Typography variant="body2" color="textSecondary">
                          Started: {new Date(scan.startTime).toLocaleTimeString()}
                        </Typography>
                        {scan.error && (
                          <Alert severity="error" sx={{ mt: 1, borderRadius: 1 }}>
                            {scan.error}
                          </Alert>
                        )}
                      </Box>
                      <Box sx={{ minWidth: { xs: '100%', sm: 250 } }}>
                        <LinearProgress
                          variant="determinate"
                          value={scan.progress}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                            '& .MuiLinearProgress-bar': {
                              bgcolor:
                                scan.status === 'failed'
                                  ? theme.palette.error.main
                                  : theme.palette.warning.main
                            }
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {scan.progress}% (
                          {(scan.results?.passedTests || 0) + (scan.results?.failedTests || 0)}/
                          {scan.results?.totalTests || 0} tests)
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => {
                          setSelectedScan(scan);
                          setDetailsDialogOpen(true);
                        }}
                        sx={{ minWidth: 100 }}
                      >
                        View
                      </Button>
                    </Stack>
                  </Paper>
                </Zoom>
              ))}
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* Prompt Injection Database Integration */}
      <Fade in timeout={700}>
        <Card
          elevation={2}
          sx={{
            mb: 3,
            borderRadius: 3,
            border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              p: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.15)} 100%)`,
              borderBottom: `2px solid ${alpha(theme.palette.success.main, 0.3)}`
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: alpha(theme.palette.success.main, 0.15),
                    color: 'success.main',
                    border: `2px solid ${theme.palette.success.main}`
                  }}
                >
                  <Shield sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Prompt Injection Database
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Test prompts against known injection patterns
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1}>
                {databaseStats && (
                  <Chip
                    label={`${databaseStats.totalPatterns} patterns`}
                    color="success"
                    sx={{ fontWeight: 700 }}
                  />
                )}
                <Button
                  startIcon={<BugReport />}
                  onClick={() => setDatabaseDialogOpen(true)}
                  variant="contained"
                  color="success"
                  size="small"
                  sx={{
                    boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`
                  }}
                >
                  Test Prompt
                </Button>
              </Stack>
            </Stack>
          </Box>
          <CardContent sx={{ p: 3 }}>
            {databaseStats ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Zoom in timeout={300}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        textAlign: 'center',
                        borderRadius: 2,
                        border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}
                      >
                        {databaseStats.totalPatterns}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>
                        Total Patterns
                      </Typography>
                    </Paper>
                  </Zoom>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Zoom in timeout={400}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        textAlign: 'center',
                        borderRadius: 2,
                        border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.12)} 100%)`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 16px ${alpha(theme.palette.secondary.main, 0.2)}`
                        }
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, color: 'secondary.main', mb: 0.5 }}
                      >
                        {databaseStats.totalCategories}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>
                        Categories
                      </Typography>
                    </Paper>
                  </Zoom>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Zoom in timeout={500}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.5)
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                        Pattern Categories
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {Object.entries(databaseStats.categoryStats || {}).map(
                          ([category, count]: [string, any]) => (
                            <Chip
                              key={category}
                              label={`${category.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}: ${count}`}
                              size="small"
                              variant="outlined"
                              color="success"
                              sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                            />
                          )
                        )}
                      </Box>
                    </Paper>
                  </Zoom>
                </Grid>
              </Grid>
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" py={2}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                  Loading database statistics...
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Fade>

      {/* Completed Scans */}
      <Fade in timeout={800}>
        <Card
          elevation={2}
          sx={{
            borderRadius: 3,
            border: `2px solid ${alpha(theme.palette.info.main, 0.2)}`,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              p: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.info.main, 0.15)} 100%)`,
              borderBottom: `2px solid ${alpha(theme.palette.info.main, 0.3)}`
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: alpha(theme.palette.info.main, 0.15),
                    color: 'info.main',
                    border: `2px solid ${theme.palette.info.main}`
                  }}
                >
                  <Assessment sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Scan History
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed security scans and results
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={`${completedScans.length} Scans`}
                  color="info"
                  sx={{ fontWeight: 700 }}
                />
                <Tooltip title="Refresh history">
                  <IconButton
                    onClick={fetchUserScans}
                    sx={{
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2) }
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
          <CardContent sx={{ p: 3 }}>
            {completedScans.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 2,
                  border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
                  bgcolor: alpha(theme.palette.background.default, 0.5)
                }}
              >
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    margin: '0 auto',
                    mb: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: 'info.main'
                  }}
                >
                  <Security sx={{ fontSize: 48 }} />
                </Avatar>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                  No Scans Completed Yet
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Start your first model security scan to see results here.
                </Typography>
              </Paper>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.1)} 100%)`,
                        '& th': {
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5
                        }
                      }}
                    >
                      <TableCell>Scan ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Tests</TableCell>
                      <TableCell>Vulnerabilities</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {completedScans.map((scan, index) => (
                      <Fade in timeout={200 + index * 50} key={scan.id}>
                        <TableRow
                          hover
                          sx={{
                            '&:hover': {
                              bgcolor: alpha(theme.palette.info.main, 0.04)
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                            >
                              {scan.id.substring(0, 8)}...
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {new Date(scan.startTime).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={scan.status.toUpperCase()}
                              color={scan.status === 'completed' ? 'success' : 'error'}
                              size="small"
                              sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {scan.duration ? `${Math.round(scan.duration / 1000)}s` : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {scan.results?.totalTests || 0} total
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {scan.results?.passedTests || 0} passed,{' '}
                              {scan.results?.failedTests || 0} failed
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {(scan.results?.vulnerabilities || [])
                                .slice(0, 3)
                                .map((vuln, idx) => (
                                  <Chip
                                    key={idx}
                                    label={vuln.severity.toUpperCase()}
                                    color={getSeverityColor(vuln.severity)}
                                    size="small"
                                    sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                                  />
                                ))}
                              {(scan.results?.vulnerabilities?.length || 0) > 3 && (
                                <Chip
                                  label={`+${(scan.results?.vulnerabilities?.length || 0) - 3}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <Tooltip title="View Details" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedScan(scan);
                                    setDetailsDialogOpen(true);
                                  }}
                                  sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Export JSON" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => exportResults(scan.id, 'json')}
                                  sx={{
                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.success.main, 0.2),
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <Download fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      </Fade>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Fade>

      {/* Configuration Dialog */}
      <Dialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Configure Model Security Scan</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Template Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Quick Start Template</InputLabel>
                <Select value="" onChange={(e) => loadTemplate(e.target.value)}>
                  {Object.entries(templates).map(([key, template]) => (
                    <MenuItem key={key} value={key}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Basic Configuration */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Model Endpoint URL"
                value={config.endpoint}
                onChange={(e) => setConfig({ ...config, endpoint: e.target.value })}
                placeholder="https://api.example.com/v1/chat/completions"
                required
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Model Type</InputLabel>
                <Select
                  value={config.modelType}
                  onChange={(e) => setConfig({ ...config, modelType: e.target.value as any })}
                >
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="anthropic">Anthropic</MenuItem>
                  <MenuItem value="huggingface">Hugging Face</MenuItem>
                  <MenuItem value="custom">Custom API</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Model Name"
                value={config.modelName}
                onChange={(e) => setConfig({ ...config, modelName: e.target.value })}
                placeholder="gpt-3.5-turbo"
              />
            </Grid>

            {/* Scan Types */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Scan Types
              </Typography>
              <FormGroup row>
                {Object.entries(scanTypes).map(([key, scanType]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={config.scanTypes.includes(key)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setConfig({ ...config, scanTypes: [...config.scanTypes, key] });
                          } else {
                            setConfig({
                              ...config,
                              scanTypes: config.scanTypes.filter((type) => type !== key)
                            });
                          }
                        }}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        {getScanTypeIcon(key)}
                        <Box>
                          <Typography variant="body2">{scanType.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {scanType.testCount} tests, {scanType.severity} severity
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </Grid>

            {/* Headers */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="HTTP Headers"
                multiline
                rows={4}
                value={headerInput}
                onChange={(e) => setHeaderInput(e.target.value)}
                onBlur={updateHeaders}
                placeholder="Authorization: Bearer your-api-key&#10;Content-Type: application/json"
                helperText="One header per line in format: Key: Value"
              />
            </Grid>

            {/* Advanced Settings */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Timeout (ms)"
                type="number"
                value={config.timeout}
                onChange={(e) => setConfig({ ...config, timeout: parseInt(e.target.value) })}
                inputProps={{ min: 1000, max: 120000 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Request Delay (ms)"
                type="number"
                value={config.requestDelay}
                onChange={(e) => setConfig({ ...config, requestDelay: parseInt(e.target.value) })}
                inputProps={{ min: 100, max: 10000 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Max Tokens"
                type="number"
                value={config.maxTokens}
                onChange={(e) => setConfig({ ...config, maxTokens: parseInt(e.target.value) })}
                inputProps={{ min: 1, max: 4000 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Temperature"
                type="number"
                value={config.temperature}
                onChange={(e) => setConfig({ ...config, temperature: parseFloat(e.target.value) })}
                inputProps={{ min: 0, max: 2, step: 0.1 }}
              />
            </Grid>

            {/* Custom Parameters for Custom API */}
            {config.modelType === 'custom' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Custom Parameters (JSON)"
                  multiline
                  rows={3}
                  value={paramInput}
                  onChange={(e) => setParamInput(e.target.value)}
                  onBlur={updateCustomParameters}
                  placeholder='{"max_length": 1000, "do_sample": true}'
                  helperText="Additional parameters to send with requests"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={startScan}
            variant="contained"
            disabled={!config.endpoint || config.scanTypes.length === 0 || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
          >
            {loading ? 'Starting...' : 'Start Scan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scan Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Scan Details - {selectedScan?.id.substring(0, 8)}...</DialogTitle>
        <DialogContent>
          {selectedScan && (
            <Box>
              {/* Scan Overview */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        {selectedScan.results?.totalTests || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Tests
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="success.main">
                        {selectedScan.results?.passedTests || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Passed
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="error.main">
                        {selectedScan.results?.failedTests || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Failed
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="warning.main">
                        {selectedScan.results?.vulnerabilities?.length || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Vulnerabilities
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Vulnerabilities */}
              {(selectedScan.results?.vulnerabilities?.length || 0) > 0 && (
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">
                      Security Vulnerabilities ({selectedScan.results?.vulnerabilities?.length || 0}
                      )
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {(selectedScan.results?.vulnerabilities || []).map((vuln, index) => (
                        <ListItem key={index} divider>
                          <ListItemIcon>
                            <Warning color={getSeverityColor(vuln.severity) as any} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle1">{vuln.test}</Typography>
                                <Chip
                                  label={vuln.severity.toUpperCase()}
                                  color={getSeverityColor(vuln.severity) as any}
                                  size="small"
                                />
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="textSecondary">
                                  {vuln.description}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  <strong>Recommendation:</strong> {vuln.recommendation}
                                </Typography>
                                {vuln.evidence && (
                                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                                    <strong>Evidence:</strong> {vuln.evidence}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Recommendations */}
              {(selectedScan.results?.recommendations?.length || 0) > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">
                      Recommendations ({selectedScan.results?.recommendations?.length || 0})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {(selectedScan.results?.recommendations || []).map((rec, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircle color="info" />
                          </ListItemIcon>
                          <ListItemText primary={rec} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Test Categories */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">
                    Test Categories ({Object.keys(selectedScan.results?.categories || {}).length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {Object.entries(selectedScan.results?.categories || {}).map(
                    ([categoryName, categoryData]: [string, any]) => (
                      <Paper key={categoryName} sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          {categoryName}
                        </Typography>
                        <Box display="flex" gap={2} mb={2}>
                          <Chip
                            label={`${categoryData.passed} Passed`}
                            color="success"
                            size="small"
                          />
                          <Chip
                            label={`${categoryData.failed} Failed`}
                            color="error"
                            size="small"
                          />
                        </Box>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Test</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Severity</TableCell>
                                <TableCell>Response Time</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {categoryData.tests.map((test: any, idx: number) => (
                                <TableRow key={idx}>
                                  <TableCell>{test.name}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={test.passed ? 'PASS' : 'FAIL'}
                                      color={test.passed ? 'success' : 'error'}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={test.severity?.toUpperCase() || 'UNKNOWN'}
                                      color={getSeverityColor(test.severity) as any}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {test.responseTime ? `${test.responseTime}ms` : '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    )
                  )}
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          {selectedScan && selectedScan.status === 'completed' && (
            <>
              <Button
                startIcon={<Download />}
                onClick={() => exportResults(selectedScan.id, 'csv')}
              >
                Export CSV
              </Button>
              <Button
                startIcon={<Download />}
                onClick={() => exportResults(selectedScan.id, 'json')}
                variant="contained"
              >
                Export JSON
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Database Test Dialog */}
      <Dialog
        open={databaseDialogOpen}
        onClose={() => setDatabaseDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <BugReport color="primary" />
            Test Prompt Against Injection Database
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Enter prompt to test"
              value={testPrompt}
              onChange={(e) => setTestPrompt(e.target.value)}
              placeholder="Enter a prompt to test for injection patterns..."
              variant="outlined"
            />

            <Box sx={{ mt: 2, mb: 2 }}>
              <Button
                variant="contained"
                onClick={testPromptInjection}
                disabled={testLoading || !testPrompt.trim()}
                startIcon={testLoading ? <CircularProgress size={16} /> : <Security />}
                fullWidth
              >
                {testLoading ? 'Analyzing...' : 'Analyze Prompt'}
              </Button>
            </Box>

            {testResult && (
              <Paper sx={{ p: 2, mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Analysis Results
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor:
                          testResult.recommendation.action === 'BLOCK'
                            ? 'error.light'
                            : 'success.light'
                      }}
                    >
                      <Typography
                        variant="h6"
                        color={
                          testResult.recommendation.action === 'BLOCK'
                            ? 'error.contrastText'
                            : 'success.contrastText'
                        }
                      >
                        {testResult.recommendation.action}
                      </Typography>
                      <Typography
                        variant="body2"
                        color={
                          testResult.recommendation.action === 'BLOCK'
                            ? 'error.contrastText'
                            : 'success.contrastText'
                        }
                      >
                        Recommendation
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="primary.main">
                        {(testResult.analysis.injection.threatScore * 100).toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Injection Score
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" color="warning.main">
                        {(testResult.analysis.security.riskScore * 100).toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Risk Score
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Alert
                  severity={testResult.recommendation.action === 'BLOCK' ? 'error' : 'success'}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle2">{testResult.recommendation.reason}</Typography>
                  <Typography variant="body2">
                    {testResult.analysis.security.recommendation}
                  </Typography>
                </Alert>

                {testResult.analysis.injection.detected && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1">
                        Detected Injection Patterns (
                        {testResult.analysis.injection.categories.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {testResult.analysis.injection.categories.map((category: string) => (
                          <Chip
                            key={category}
                            label={category
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, (l: string) => l.toUpperCase())}
                            color="error"
                            size="small"
                          />
                        ))}
                      </Box>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Risk Level: <strong>{testResult.analysis.injection.highestSeverity}</strong>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDatabaseDialogOpen(false);
              setTestResult(null);
              setTestPrompt('');
            }}
          >
            Close
          </Button>
          {testResult && (
            <Button
              startIcon={<ContentCopy />}
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(testResult, null, 2));
                toast.success('Results copied to clipboard');
              }}
            >
              Copy Results
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ModelScanner;
